<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useDebounce } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterButton from 'dashboard/components/ui/Dropdown/DropdownButton.vue';
import FilterListDropdown from 'dashboard/components/ui/Dropdown/DropdownList.vue';
import twentyApi from 'dashboard/api/integrations/twenty';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contactName: { type: String, default: '' },
  contactEmail: { type: String, default: '' },
  contactPhone: { type: String, default: '' },
  instanceUrl: { type: String, default: '' },
});

const emit = defineEmits(['linked', 'close']);

const TWENTY_LOGO = '/dashboard/images/integrations/twenty.png';

const { t } = useI18n();

const selectedTabIndex = ref(0);
const tabs = ref([
  {
    key: 0,
    name: t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.TAB_CREATE'),
  },
  {
    key: 1,
    name: t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.TAB_LINK'),
  },
]);

const inputStyles = { borderRadius: '0.75rem', fontSize: '0.875rem' };

// ─── link tab ─────────────────────────────────────────────────────────────────
const shouldShowDropdown = ref(false);
const selectedPerson = ref(null);
const personList = ref([]);
const isFetching = ref(false);
const isLinking = ref(false);

const linkPersonTitle = computed(
  () =>
    selectedPerson.value?.name ||
    t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.SELECT_PERSON')
);

const isSubmitDisabled = computed(
  () => !selectedPerson.value || isLinking.value
);

const toggleDropdown = () => {
  shouldShowDropdown.value = !shouldShowDropdown.value;
};

const formatPersonList = persons =>
  persons.map(p => ({
    id: p.id,
    name: p.company ? `${p.name} | ${p.company}` : p.name,
    personName: p.name,
    subtitle: p.subtitle,
    company: p.company,
  }));

const onSearch = async value => {
  isFetching.value = true;
  try {
    const { data } = await twentyApi.searchPeople(value);
    personList.value = formatPersonList(data?.data || []);
  } catch {
    personList.value = [];
  } finally {
    isFetching.value = false;
  }
};

const onSelectPerson = item => {
  selectedPerson.value = item;
  shouldShowDropdown.value = false;
};

const linkExisting = async record => {
  isLinking.value = true;
  try {
    await twentyApi.linkRecord({
      conversationId: props.conversationId,
      recordId: record.id,
      recordType: 'person',
      recordName: record.personName || record.name,
      recordData: {
        email: record.subtitle,
        company: record.company,
        phone: props.contactPhone || undefined,
      },
    });
    emit('linked');
  } finally {
    isLinking.value = false;
  }
};

const linkSelected = async () => {
  if (!selectedPerson.value) return;
  await linkExisting(selectedPerson.value);
};

// ─── create tab ───────────────────────────────────────────────────────────────
const form = ref({ firstName: '', lastName: '', email: '', phone: '' });
const isSaving = ref(false);
const createError = ref('');
const duplicateCandidates = ref([]);
const isCheckingDuplicates = ref(false);

const isFormEmpty = computed(
  () =>
    !form.value.firstName &&
    !form.value.lastName &&
    !form.value.email &&
    !form.value.phone
);

// ─── company ──────────────────────────────────────────────────────────────────
const companyId = ref(null);
const selectedCompanyName = ref('');
const companyQuery = ref('');
const debouncedCompanyQuery = useDebounce(companyQuery, 400);
const companyResults = ref([]);
const isSearchingCompany = ref(false);

const runCompanySearch = async query => {
  if (!query || query.length < 2) {
    companyResults.value = [];
    return;
  }
  isSearchingCompany.value = true;
  try {
    const { data } = await twentyApi.searchCompanies(query);
    companyResults.value = (data?.data || []).filter(r => r.type === 'company');
  } finally {
    isSearchingCompany.value = false;
  }
};

watch(debouncedCompanyQuery, runCompanySearch);

const selectCompany = company => {
  companyId.value = company.id;
  selectedCompanyName.value = company.name;
  companyQuery.value = '';
  companyResults.value = [];
};

const clearCompany = () => {
  companyId.value = null;
  selectedCompanyName.value = '';
  companyQuery.value = '';
  companyResults.value = [];
};

const openCompany = () => {
  if (!companyId.value || !props.instanceUrl) return;
  window.open(
    `${props.instanceUrl}/object/company/${companyId.value}`,
    '_blank'
  );
};

const createCompanyLabel = computed(() => {
  const base = t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.CREATE_COMPANY');
  const label = companyQuery.value.trim()
    ? `${base} "${companyQuery.value}"`
    : base;
  return `+ ${label}`;
});

const createAndSelectCompany = async name => {
  if (!name.trim()) return;
  try {
    const { data } = await twentyApi.createCompany(name.trim());
    selectCompany(data.data);
    companyQuery.value = '';
  } catch {
    // silently handled
  }
};

// ─── duplicate check + save ───────────────────────────────────────────────────
const proceedWithCreate = async () => {
  isSaving.value = true;
  createError.value = '';
  try {
    await twentyApi.createPerson({
      conversationId: props.conversationId,
      firstName: form.value.firstName,
      lastName: form.value.lastName,
      email: form.value.email,
      companyId: companyId.value || undefined,
      phone: form.value.phone || undefined,
    });

    emit('linked');
  } catch {
    createError.value = t(
      'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.CREATE_ERROR'
    );
    duplicateCandidates.value = [];
  } finally {
    isSaving.value = false;
  }
};

const savePerson = async () => {
  const fullName = `${form.value.firstName} ${form.value.lastName}`.trim();
  if (!fullName && !form.value.email && !form.value.phone) return;

  if (fullName) {
    isCheckingDuplicates.value = true;
    try {
      const { data } = await twentyApi.searchPeople(fullName);
      const matches = (data?.data || []).filter(p =>
        p.name.toLowerCase().includes(fullName.toLowerCase())
      );
      if (matches.length) {
        duplicateCandidates.value = matches;
        return;
      }
    } catch {
      // ignore search errors, proceed with create
    } finally {
      isCheckingDuplicates.value = false;
    }
  }

  await proceedWithCreate();
};

const onClose = () => emit('close');

onMounted(async () => {
  const parts = (props.contactName || '').split(' ');
  form.value.firstName = parts[0] || '';
  form.value.lastName = parts.slice(1).join(' ') || '';
  form.value.email = props.contactEmail || '';
  form.value.phone = props.contactPhone || '';
  await onSearch(props.contactEmail || '');
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="
        $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.MODAL_TITLE')
      "
      :header-content="
        $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.MODAL_DESCRIPTION')
      "
    />

    <div class="flex flex-col h-auto overflow-auto">
      <div class="flex flex-col px-4 sm:px-8 pb-4 mt-1">
        <woot-tabs
          class="ltr:[&>ul]:pl-0 rtl:[&>ul]:pr-0 h-10"
          :index="selectedTabIndex"
          @change="index => (selectedTabIndex = index)"
        >
          <woot-tabs-item
            v-for="(tab, index) in tabs"
            :key="tab.key"
            :index="index"
            :name="tab.name"
            :show-badge="false"
            is-compact
          />
        </woot-tabs>
      </div>

      <!-- Create tab -->
      <div
        v-if="selectedTabIndex === 0"
        class="flex flex-col px-4 sm:px-8 pb-4"
      >
        <!-- Duplicate candidates view -->
        <template v-if="duplicateCandidates.length">
          <p class="text-sm text-n-slate-12 mb-3">
            {{
              $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.DUPLICATE_WARNING')
            }}
          </p>
          <ul class="flex flex-col gap-2 mb-2">
            <li
              v-for="p in duplicateCandidates"
              :key="p.id"
              class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 px-3 py-2 rounded-xl border border-n-weak"
            >
              <div class="min-w-0 w-full sm:w-auto">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ p.name }}
                </p>
                <p v-if="p.subtitle" class="text-xs text-n-slate-11 truncate">
                  {{ p.subtitle }}
                </p>
              </div>
              <Button
                xs
                class="w-full sm:w-auto"
                :label="
                  $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.USE_EXISTING')
                "
                :is-loading="isLinking"
                @click.prevent="linkExisting(p)"
              />
            </li>
          </ul>
          <div
            class="flex flex-col sm:flex-row items-stretch sm:items-center justify-end w-full gap-2 mt-6"
          >
            <Button
              faded
              slate
              class="w-full sm:w-auto"
              :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.BACK')"
              @click="duplicateCandidates = []"
            />
            <Button
              class="w-full sm:w-auto"
              :label="
                $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.CREATE_ANYWAY')
              "
              :is-loading="isSaving"
              @click="proceedWithCreate"
            />
          </div>
        </template>

        <!-- Normal form -->
        <template v-else>
          <woot-input
            v-model="form.firstName"
            class="w-full"
            :styles="{ ...inputStyles, padding: '0.375rem 0.75rem' }"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.FIRST_NAME')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.FIRST_NAME')
            "
          />
          <woot-input
            v-model="form.lastName"
            class="w-full"
            :styles="{ ...inputStyles, padding: '0.375rem 0.75rem' }"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.LAST_NAME')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.LAST_NAME')
            "
          />
          <woot-input
            v-model="form.email"
            type="email"
            class="w-full"
            :styles="{ ...inputStyles, padding: '0.375rem 0.75rem' }"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.EMAIL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.EMAIL')
            "
          />
          <woot-input
            v-model="form.phone"
            type="tel"
            class="w-full"
            :styles="{ ...inputStyles, padding: '0.375rem 0.75rem' }"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.PHONE')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.PHONE')
            "
          />

          <!-- Company field: label always visible -->
          <div class="flex flex-col gap-1 mb-4">
            <p class="text-sm font-medium leading-7 text-n-slate-12 mb-0">
              {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.COMPANY') }}
            </p>

            <!-- Selected company: Twenty-style card -->
            <template v-if="companyId">
              <div
                class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mt-1"
              >
                <div
                  class="flex items-center gap-2 px-2 py-1.5 border rounded-lg border-n-strong flex-1 min-w-0"
                >
                  <div class="flex items-center gap-1 min-w-0">
                    <img
                      :src="TWENTY_LOGO"
                      class="size-4 shrink-0"
                      alt="Twenty"
                    />
                    <span class="text-xs font-medium text-n-slate-12 truncate">
                      {{ selectedCompanyName }}
                    </span>
                  </div>
                  <span class="w-px h-3 text-n-weak bg-n-weak shrink-0" />
                  <Button
                    link
                    xs
                    slate
                    icon="i-lucide-arrow-up-right"
                    class="!size-4 shrink-0"
                    @click="openCompany"
                  />
                </div>
                <Button
                  ghost
                  xs
                  slate
                  icon="i-lucide-x"
                  class="self-end sm:self-auto"
                  @click="clearCompany"
                />
              </div>
            </template>

            <!-- Company search input + dropdown -->
            <template v-else>
              <woot-input
                v-model="companyQuery"
                class="w-full"
                :styles="{ ...inputStyles, padding: '0.375rem 0.75rem' }"
                :placeholder="
                  $t(
                    'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.COMPANY_SEARCH_PLACEHOLDER'
                  )
                "
              />
              <ul
                v-if="companyResults.length"
                class="flex flex-col border border-n-weak rounded-xl overflow-hidden"
              >
                <li
                  v-for="c in companyResults"
                  :key="c.id"
                  class="px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer"
                  @click="selectCompany(c)"
                >
                  {{ c.name }}
                </li>
                <li
                  v-if="companyQuery.trim()"
                  class="px-3 py-2 text-sm text-woot-500 hover:bg-n-alpha-2 cursor-pointer border-t border-n-weak"
                  @click="createAndSelectCompany(companyQuery)"
                >
                  {{ createCompanyLabel }}
                </li>
              </ul>
              <button
                v-else-if="companyQuery.trim() && !isSearchingCompany"
                class="text-left text-xs text-woot-500 hover:text-woot-600 transition-colors mt-1"
                @click="createAndSelectCompany(companyQuery)"
              >
                {{ createCompanyLabel }}
              </button>
            </template>
          </div>

          <p v-if="createError" class="text-xs text-ruby-500 mt-1">
            {{ createError }}
          </p>

          <div
            class="flex flex-col sm:flex-row items-stretch sm:items-center justify-end w-full gap-2 mt-6"
          >
            <Button
              faded
              slate
              class="w-full sm:w-auto"
              :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.CANCEL')"
              @click.prevent="onClose"
            />
            <Button
              class="w-full sm:w-auto"
              :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.SAVE')"
              :disabled="isFormEmpty || isSaving || isCheckingDuplicates"
              :is-loading="isSaving || isCheckingDuplicates"
              @click.prevent="savePerson"
            />
          </div>
        </template>
      </div>

      <!-- Link existing tab -->
      <div
        v-else
        class="flex flex-col px-4 sm:px-8 pb-4"
        :class="shouldShowDropdown ? 'h-[280px]' : 'gap-2'"
      >
        <FilterButton
          trailing-icon
          icon="i-lucide-chevron-down"
          :button-text="linkPersonTitle"
          class="justify-between w-full h-[2.5rem] py-1.5 px-3 rounded-xl bg-n-alpha-black2 outline outline-1 outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6"
          @click="toggleDropdown"
        >
          <template v-if="shouldShowDropdown" #dropdown>
            <FilterListDropdown
              v-on-clickaway="toggleDropdown"
              :show-clear-filter="false"
              :list-items="personList"
              :active-filter-id="selectedPerson?.id"
              :is-loading="isFetching"
              :input-placeholder="
                $t(
                  'INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.LINK_SEARCH_PLACEHOLDER'
                )
              "
              :loading-placeholder="
                $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.LOADING')
              "
              enable-search
              class="left-0 flex flex-col w-full overflow-y-auto h-fit !max-h-[160px] md:left-auto md:right-0 top-10"
              @on-search="onSearch"
              @select="onSelectPerson"
            />
          </template>
        </FilterButton>

        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center justify-end w-full gap-2 mt-2"
        >
          <Button
            faded
            slate
            class="w-full sm:w-auto"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.CANCEL')"
            @click.prevent="onClose"
          />
          <Button
            class="w-full sm:w-auto"
            :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.LINK_BUTTON')"
            :disabled="isSubmitDisabled"
            :is-loading="isLinking"
            @click.prevent="linkSelected"
          />
        </div>
      </div>
    </div>
  </div>
</template>
